/**
 * useTransientState (OCP-538) - state that auto-reverts after a delay,
 * with the timer bound to the component lifecycle.
 *
 * Replaces the bare `setX(v); setTimeout(() => setX(reset), ms)` pattern
 * used for copy-confirmations and action-flash messages. That pattern
 * leaks its timeout past unmount: the late callback calls setState after
 * the test environment (or page) is torn down, which under CI runner
 * load surfaced as an unhandled react-dom "window is not defined" and
 * failed the whole admin suite despite every test passing
 * (AdminServerDetailPage, run 2293). One shared, tested hook instead of
 * N hand-written timers - per the fix-the-class rule.
 *
 * Usage:
 *   const [copied, flashCopied] = useTransientState(false, 1500);
 *   flashCopied(true);        // sets true, reverts to false after 1.5s
 *
 * Re-triggering restarts the timer (last call wins). The revert value is
 * the initial value. On unmount the pending timer is cancelled.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useTransientState<T>(
  initialValue: T,
  resetAfterMs: number,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(initialValue);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initial = useRef(initialValue);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const trigger = useCallback(
    (v: T) => {
      setValue(v);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        setValue(initial.current);
      }, resetAfterMs);
    },
    [resetAfterMs],
  );

  return [value, trigger];
}
